import { getActivePartnerId } from "@/providers/dataProvider/dataProvider";
import { getAuthProvider } from "@/providers/authProvider/authProviderInstance";
import { USE_AUTH } from "@/config/auth";
import { API_CONSTANTS } from "@/constants";

/**
 * Build the headers for a raw document-download fetch — the binary/blob downloads
 * that bypass the react-admin httpClient. Mirrors what httpClient sends:
 *  - `X-Partner-ID` scoped to the active partner (superadmin "acting as" override,
 *    else the user's home partner), so the backend resolves ownership against the
 *    partner the user is acting as; a missing header here is exactly bug #5350.
 *  - the MSAL bearer token when auth is enabled.
 *
 * Centralizing this keeps every document-download path partner-scoped by
 * construction, instead of the header being copy-pasted (and silently droppable)
 * into each raw fetch.
 */
export const buildDocumentDownloadHeaders = async (): Promise<Record<string, string>> => {
  const headers: Record<string, string> = { Accept: "application/octet-stream" };

  const partnerId = getActivePartnerId();
  if (partnerId) {
    headers[API_CONSTANTS.HEADERS.PARTNER_ID] = partnerId;
  }

  if (USE_AUTH) {
    try {
      const authProvider = getAuthProvider();
      if (authProvider?.getAccessToken) {
        const token = await authProvider.getAccessToken();
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }
      }
    } catch (authError) {
      console.error("[buildDocumentDownloadHeaders] Error getting access token:", authError);
    }
  }

  return headers;
};

/**
 * Fetch a document-download endpoint with partner-scoped, authenticated headers and
 * return the raw Response on success (callers pick `.blob()` / `.arrayBuffer()`).
 * Throws a consistent error on a non-OK status. Use this for every raw binary
 * document fetch so the `X-Partner-ID` header can never be silently dropped (#5350).
 */
export const fetchDocumentResource = async (url: string, init?: { signal?: AbortSignal }): Promise<Response> => {
  const headers = await buildDocumentDownloadHeaders();
  const response = await fetch(url, { headers, signal: init?.signal });
  if (!response.ok) {
    throw new Error(`Failed to download document: ${response.statusText}`);
  }
  return response;
};
